import { juntarClasses } from './classes';
import { IconeEspiral } from './icones';

/**
 * Bloco cinza no lugar do conteúdo que está vindo.
 *
 * O pulso é enfeite: sob `prefers-reduced-motion` a regra global zera a duração
 * e o bloco fica parado — e continua funcionando, porque o significado vem da
 * forma e do texto de estado que acompanha, nunca do movimento.
 */
export interface PropsEsqueleto {
  /** Classes de tamanho: `h-4 w-32`, `h-24 w-full`. */
  className?: string;
  circulo?: boolean;
}

export function Esqueleto({ className, circulo = false }: PropsEsqueleto): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className={juntarClasses(
        'block animate-pulse bg-superficie-vidro',
        circulo ? 'rounded-full' : 'rounded-card',
        className ?? 'h-4 w-full',
      )}
    />
  );
}

export type VarianteCarregando = 'inline' | 'texto' | 'cartao' | 'tabela' | 'painel';

export interface PropsEstadoCarregando {
  /**
   * O que está carregando, em minúscula e com artigo: "os contratos de 2026".
   * Vira "Carregando os contratos de 2026…" — texto real, não "Loading...".
   */
  contexto?: string;
  variante?: VarianteCarregando;
  /** Linhas do esqueleto nas variantes `texto` e `tabela`. */
  linhas?: number;
  className?: string;
}

/**
 * Estado de carregamento.
 *
 * `role="status"` + `aria-live="polite"`: o leitor de tela anuncia o que está
 * vindo em vez de ficar em silêncio na tela vazia — o outro lado da lição do
 * "nunca tela preta" (§8, dívida #38).
 */
export function EstadoCarregando({
  contexto,
  variante = 'texto',
  linhas = 3,
  className,
}: PropsEstadoCarregando): JSX.Element {
  const mensagem = contexto === undefined ? 'Carregando…' : `Carregando ${contexto}…`;
  const quantidade = Math.max(1, linhas);

  if (variante === 'inline') {
    return (
      <span
        role="status"
        aria-live="polite"
        className={juntarClasses(
          'inline-flex items-center gap-2 text-sm text-conteudo-secundario',
          className,
        )}
      >
        <IconeEspiral />
        {mensagem}
      </span>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={juntarClasses(
        variante === 'cartao' || variante === 'painel'
          ? 'rounded-painel border border-borda bg-superficie-elevada p-6'
          : '',
        className,
      )}
    >
      <span className="sr-only">{mensagem}</span>

      {variante === 'tabela' ? (
        <div className="flex flex-col gap-px overflow-hidden rounded-card border border-borda">
          <div className="flex gap-4 bg-superficie-vidro/50 px-4 py-3">
            <Esqueleto className="h-3 w-40" />
            <Esqueleto className="ml-auto h-3 w-24" />
            <Esqueleto className="h-3 w-24" />
          </div>
          {Array.from({ length: quantidade }, (_, indice) => (
            <div key={indice} className="flex items-center gap-4 bg-superficie-elevada px-4 py-3">
              <Esqueleto className="h-3 w-56" />
              <Esqueleto className="ml-auto h-3 w-20" />
              <Esqueleto className="h-3 w-20" />
            </div>
          ))}
        </div>
      ) : variante === 'painel' ? (
        <div className="flex flex-col gap-4">
          <Esqueleto className="h-6 w-48" />
          <Esqueleto className="h-28 w-full" />
          <div className="flex gap-3">
            <Esqueleto className="h-9 w-28" />
            <Esqueleto className="h-9 w-28" />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: quantidade }, (_, indice) => (
            <Esqueleto
              key={indice}
              // A última linha mais curta imita parágrafo — o bloco não parece
              // um retângulo genérico à espera de nada.
              className={juntarClasses('h-4', indice === quantidade - 1 ? 'w-2/3' : 'w-full')}
            />
          ))}
        </div>
      )}

      {contexto !== undefined && variante !== 'tabela' && (
        <p aria-hidden="true" className="mt-4 text-xs text-conteudo-secundario">
          {mensagem}
        </p>
      )}
    </div>
  );
}
